import { useMemo, useState } from "react";
import { toast } from "sonner";
import useRealtime from "../../lib/useRealtime";
import api, { formatApiError } from "../../lib/api";
import ScRequestTypeToggle from "../../components/sc/ScRequestTypeToggle";
import { DURATION_FILTERS, inDurationWindow } from "../../lib/dateRangeFilters";
import { TICKET_TYPE_LABEL, ticketCompletionPct, isTicketInProblem } from "../../lib/ticketUtils";
import { TICKET_STATUS_KEY } from "../../components/customer-app/ui";
import TicketPhotoGallery from "../../components/crm/TicketPhotoGallery";
import { Plus, X, Wrench, AlertTriangle, CheckCircle2, Loader2, ChevronDown, Camera } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const TICKET_STATUS_BADGE = {
  open:                      { bg: "bg-sky-50",     fg: "text-sky-700",   label: "Open" },
  in_progress:               { bg: "bg-amber-50",   fg: "text-amber-700", label: "In Progress" },
  on_hold:                   { bg: "bg-orange-50",  fg: "text-orange-700",label: "On Hold" },
  resolution_support_required:{ bg: "bg-rose-50",   fg: "text-rose-700",  label: "Needs Support" },
  completed:                 { bg: "bg-emerald-50", fg: "text-[#1B5E20]", label: "Completed" },
  closed:                    { bg: "bg-emerald-50", fg: "text-[#1B5E20]", label: "Closed" },
  cancelled:                 { bg: "bg-slate-100",  fg: "text-slate-500", label: "Cancelled" },
  dropped:                   { bg: "bg-slate-200",  fg: "text-slate-600", label: "Dropped" },
};

const REQUEST_TYPES = [
  { value: "operational_issue", label: "Operational Issue" },
  { value: "repair_maintenance", label: "Repair / Maintenance" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function NewRequestSheet({ customers, onClose, onCreated }) {
  const [form, setForm] = useState({ customer_id: "", title: "", description: "", type: "operational_issue" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!form.customer_id) { setError("Pick which customer this is for."); return; }
    setSaving(true);
    setError("");
    try {
      await api.post("/sc/service-requests", form);
      toast.success("Service request raised — the service team has been notified");
      onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/50" data-testid="sc-new-request-sheet">
      <div className="w-full max-w-md bg-white rounded-t-2xl p-5 pb-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-slate-900">Raise Service Request</h2>
          <button type="button" onClick={onClose} data-testid="sc-new-request-close" className="p-1 text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3.5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Customer *</label>
            <div className="relative">
              <select
                required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                data-testid="sc-new-request-customer"
                className="w-full appearance-none border-2 border-slate-200 focus:border-[#1B5E20] rounded-xl px-3 py-2.5 text-sm outline-none bg-white"
              >
                <option value="">Select a customer you referred…</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>{c.customer_name}{c.phone ? ` · ${c.phone}` : ""}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3 pointer-events-none" />
            </div>
            {customers.length === 0 && (
              <div className="text-[11px] text-amber-600 mt-1">You don't have any referred customers yet.</div>
            )}
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Issue Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {REQUEST_TYPES.map((t) => (
                <button
                  type="button" key={t.value} onClick={() => setForm({ ...form, type: t.value })}
                  data-testid={`sc-new-request-type-${t.value}`}
                  className={"px-2.5 py-2 rounded-lg text-xs font-semibold border-2 transition-colors " +
                    (form.type === t.value ? "border-[#1B5E20] bg-emerald-50 text-[#1B5E20]" : "border-slate-200 text-slate-600")}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Title *</label>
            <input
              required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="sc-new-request-title" placeholder="e.g. Inverter showing fault light"
              className="w-full border-2 border-slate-200 focus:border-[#1B5E20] rounded-xl px-3 py-2.5 text-sm outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Details</label>
            <textarea
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="sc-new-request-description" placeholder="What did the customer tell you?" rows={3}
              className="w-full border-2 border-slate-200 focus:border-[#1B5E20] rounded-xl px-3 py-2.5 text-sm outline-none resize-none"
            />
          </div>
          {error && <div className="text-xs text-red-600" data-testid="sc-new-request-error">{error}</div>}
          <button
            type="submit" disabled={saving} data-testid="sc-new-request-submit"
            className="w-full bg-[#1B5E20] text-white py-3 rounded-xl font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            {saving ? "Raising…" : "Raise Request"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ScServiceRequests() {
  const { t } = useLanguage();
  const { data: ticketsRaw, loading, refresh } = useRealtime("/sc/service-requests");
  const { data: customersRaw } = useRealtime("/sc/my-customers");
  const tickets = useMemo(() => (Array.isArray(ticketsRaw) ? ticketsRaw : []), [ticketsRaw]);
  const customers = useMemo(() => (Array.isArray(customersRaw) ? customersRaw : []), [customersRaw]);
  const [duration, setDuration] = useState("all");
  const [showSheet, setShowSheet] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const filtered = useMemo(() => tickets.filter((t) => inDurationWindow(t.created_at, duration)), [tickets, duration]);
  const kpi = useMemo(() => ({
    open: filtered.filter((t) => !["completed", "closed", "cancelled", "dropped"].includes(t.status)).length,
    closed: filtered.filter((t) => ["completed", "closed", "cancelled", "dropped"].includes(t.status)).length,
    inProblem: filtered.filter(isTicketInProblem).length,
  }), [filtered]);

  return (
    <div data-testid="sc-service-requests">
      <ScRequestTypeToggle active="service" />

      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{t("sc.requestsTitle")}</div>
      <h1 className="text-xl font-bold tracking-tight mt-0.5 text-slate-900">
        {filtered.length} <span className="font-medium text-slate-400">{t("sc.requestsForCustomers")}</span>
      </h1>

      <div className="mt-3 flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-testid="sc-duration-filter">
        {DURATION_FILTERS.map((f) => (
          <button
            key={f.v} type="button" onClick={() => setDuration(f.v)} data-testid={`sc-duration-${f.v}`}
            className={"text-[10px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full whitespace-nowrap transition-all border " +
              (duration === f.v ? "bg-[#1B5E20] text-white border-[#1B5E20] shadow-sm" : "bg-white text-slate-600 hover:text-slate-900 border-slate-200")}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2" data-testid="sc-service-kpis">
        <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
          <div className="font-mono text-xl font-bold text-sky-700">{kpi.open}</div>
          <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 mt-0.5">{t("sc.kpiOpen")}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
          <div className="font-mono text-xl font-bold text-[#1B5E20]">{kpi.closed}</div>
          <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 mt-0.5">{t("sc.kpiResolved")}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
          <div className="font-mono text-xl font-bold text-rose-600">{kpi.inProblem}</div>
          <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 mt-0.5">{t("sc.kpiInProblem")}</div>
        </div>
      </div>

      <button
        type="button" onClick={() => setShowSheet(true)} data-testid="sc-raise-request-btn"
        className="mt-4 w-full flex items-center justify-center gap-2 bg-[#1B5E20] text-white py-3 rounded-xl font-bold text-sm hover:bg-[#15501A] active:scale-[0.99] transition-all"
      >
        <Plus className="w-4 h-4" strokeWidth={3} /> {t("sc.raiseRequest")}
      </button>

      <div className="mt-4 space-y-2.5">
        {loading && tickets.length === 0 && <div className="text-xs text-slate-400 text-center py-10">{t("common.loading")}</div>}
        {filtered.map((tk) => {
          const badge = TICKET_STATUS_BADGE[tk.status] || TICKET_STATUS_BADGE.open;
          const pct = ticketCompletionPct(tk);
          const problem = isTicketInProblem(tk);
          return (
            <div key={tk.id} data-testid={`sc-ticket-row-${tk.id}`} className={"bg-white rounded-2xl p-4 shadow-[0_2px_8px_rgba(15,23,42,0.04)] border " + (problem ? "border-rose-200" : "border-slate-200")}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[9px] font-mono text-slate-400">{tk.ticket_number}</div>
                  <div className="text-[14px] font-semibold text-slate-900 truncate mt-0.5">{tk.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {tk.customer_snapshot?.customer_name || "—"} · {TICKET_TYPE_LABEL[tk.type] || tk.type} · {fmtDate(tk.created_at)}
                  </div>
                </div>
                <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${badge.bg} ${badge.fg}`}>
                  {t("tickets.status" + (TICKET_STATUS_KEY[tk.status] || "Open"))}
                </span>
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#1B5E20] rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[10px] font-mono font-semibold text-slate-500">{pct}%</span>
              </div>
              {tk.sla_breached && (
                <div className="mt-2 flex items-center gap-1 text-[10px] text-rose-600 font-semibold" data-testid={`sc-ticket-sla-breach-${tk.id}`}>
                  <AlertTriangle className="w-3 h-3" /> {t("sc.slaBreached")}
                </div>
              )}
              <button
                type="button" onClick={() => setExpandedId(expandedId === tk.id ? null : tk.id)}
                data-testid={`sc-ticket-photos-toggle-${tk.id}`}
                className="mt-2.5 flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-[#1B5E20]"
              >
                <Camera className="w-3 h-3" /> {expandedId === tk.id ? t("sc.hidePhotos") : t("sc.viewPhotos")}
              </button>
              {expandedId === tk.id && (
                <div className="mt-2" data-testid={`sc-ticket-photos-${tk.id}`}>
                  <TicketPhotoGallery ticketId={tk.id} canUpload={false} canDelete={false} compact />
                </div>
              )}
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-2xl">
            <CheckCircle2 className="w-8 h-8 mx-auto text-slate-300" />
            <div className="text-sm text-slate-500 mt-3">{t("sc.noRequestsWindow")}</div>
          </div>
        )}
      </div>

      {showSheet && (
        <NewRequestSheet
          customers={customers}
          onClose={() => setShowSheet(false)}
          onCreated={() => { setShowSheet(false); refresh(); }}
        />
      )}
    </div>
  );
}
